import { type ReactNode } from 'react'
import cx from 'classnames'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { dateFormatters } from '@/utils/date-util'
import type { Id } from '@/infra/id'
import styles from './MembershipsTab.module.css'
import { TabBar, TabItem } from '@/components/TabBar'
import { SearchField } from '@/components/SearchField'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import {
  type AdminAccess,
  type MembershipTab,
} from '@/features/user-management/MembershipsTab/MembershipsTabViewModel'

export interface MembershipsTabProps {
  membershipsCount: number
  searchText: string
  onSearchTextChange: (value: string) => void
  addButtonTestId: string
  onAdd: () => void
  currentTab: MembershipTab
  onTabChange: (tab: MembershipTab) => void
  /**
   * Whether the current tab has no memberships to list.
   */
  isEmpty: boolean
  isSearchFilterApplied: boolean
  corporateMode: boolean
  onClearSearchFilter: () => void
  /**
   * The membership rows of the current tab.
   */
  children: ReactNode
}

export function MembershipsTab({
  membershipsCount,
  searchText,
  onSearchTextChange,
  addButtonTestId,
  onAdd,
  currentTab,
  onTabChange,
  isEmpty,
  isSearchFilterApplied,
  corporateMode,
  onClearSearchFilter,
  children,
}: MembershipsTabProps) {
  return (
    <>
      <div className={styles.membershipsCountContainer}>
        <span className={styles.membershipsLabel}>Memberships</span>
        <span className={styles.membershipsCount}>({membershipsCount})</span>
      </div>

      <div className={styles.root}>
        <div className={styles.header}>
          <SearchField
            stretch
            autoFocus
            showClearButton
            className={styles.searchBar}
            placeholder="Search"
            value={searchText}
            onChange={onSearchTextChange}
            testId="workspace-memberships-tab-search-field"
          />
          <div className={styles.headerActions}>
            <IconButton
              name="add"
              label="Add membership"
              onPress={onAdd}
              testId={addButtonTestId}
              size={24}
              color="var(--grey600)"
            />
          </div>
        </div>

        <div className={styles.membershipTabs}>
          <TabBar>
            <TabItem
              active={currentTab === 'Active'}
              onPress={() => onTabChange('Active')}
              testId="workspace-memberships-tab-active"
            >
              Active
            </TabItem>
            <TabItem
              active={currentTab === 'Inactive'}
              onPress={() => onTabChange('Inactive')}
              testId="workspace-memberships-tab-inactive"
            >
              Inactive
            </TabItem>
          </TabBar>
        </div>

        <div className={styles.membershipsList}>
          {!isEmpty ? (
            children
          ) : (
            <NoResultsEmptyState
              corporateMode={corporateMode}
              isSearchFilterApplied={isSearchFilterApplied}
              onClearSearchFilter={onClearSearchFilter}
            />
          )}
        </div>
      </div>
    </>
  )
}

export interface MembershipRowProps {
  workspaceId: Id
  workspaceName: string
  workspaceIcon: string
  roleName?: string
  dateCreated: Date
  /**
   * Whether this membership is open in the sidebar.
   */
  isActive: boolean
  isInactive: boolean
  /**
   * What the viewer may do with this membership; no actions without it.
   */
  access: AdminAccess | null
  canPromoteToOwner: boolean
  /**
   * Whether to offer a magic link instead of impersonation.
   */
  useMagicLink: boolean
  isGenerateMagicLinkPending: boolean
  onView: () => void
  onRestore: () => void
  onAddToTeam: () => void
  onEditRole: () => void
  onEditSkills: () => void
  onManageCapacity: () => void
  onRemoveFromWorkspace: () => void
  onPromoteToOwner: () => void
  onImpersonateAsClient: () => void
  onImpersonateAsPro: () => void
  onGenerateMagicLink: () => void
}

export function MembershipRow({
  workspaceId,
  workspaceName,
  workspaceIcon,
  roleName,
  dateCreated,
  isActive,
  isInactive,
  access,
  canPromoteToOwner,
  useMagicLink,
  isGenerateMagicLinkPending,
  onView,
  onRestore,
  onAddToTeam,
  onEditRole,
  onEditSkills,
  onManageCapacity,
  onRemoveFromWorkspace,
  onPromoteToOwner,
  onImpersonateAsClient,
  onImpersonateAsPro,
  onGenerateMagicLink,
}: MembershipRowProps) {
  return (
    <div className={cx(styles.membershipRow, isActive && styles.active)}>
      <div className={styles.membershipInfo}>
        <div className={isInactive ? styles.disabled : undefined}>
          <Avatar size={32} subject={{ avatar: workspaceIcon }} />
        </div>
        <div className={styles.membershipInfoText}>
          <span className={styles.membershipInfoTextTitle}>
            {workspaceName}
          </span>
          <span className={styles.membershipInfoTextSubtitle}>
            {roleName}
            <span className={styles.membershipInfoTextSubtitleDivider}>|</span>
            {dateFormatters.dateLongWithTime.format(dateCreated)}
          </span>
        </div>
      </div>
      {access && (
        <div className={styles.membershipActions}>
          {!isInactive && (
            <Button
              label="View"
              className={styles.membershipViewButton}
              variant="text"
              onPress={onView}
              testId={`memberships-row-open-sidebar-${workspaceId}`}
            />
          )}

          <Dropdown
            autoDismiss
            position="bottom-end"
            button={
              <IconButton
                name="more"
                label="Membership actions"
                className={styles.membershipActionsButton}
                testId={`memberships-row-actions-${workspaceId}`}
                size={24}
              />
            }
          >
            <DropdownTitle>Actions</DropdownTitle>
            {isInactive ? (
              <>
                {access.canManageWorkspaceMemberships && (
                  <DropdownItem
                    onPress={onRestore}
                    buttonTestId={`memberships-row-actions-restore-to-workspace-${workspaceId}`}
                  >
                    Restore membership
                  </DropdownItem>
                )}
              </>
            ) : (
              <>
                {access.canAddUserToTeam && (
                  <DropdownItem
                    onPress={onAddToTeam}
                    buttonTestId="memberships-row-actions-add-to-team"
                  >
                    Add user to team
                  </DropdownItem>
                )}
                {access.canEditUserRole && (
                  <DropdownItem
                    onPress={onEditRole}
                    buttonTestId={`memberships-row-actions-edit-role-${workspaceId}`}
                  >
                    Edit role
                  </DropdownItem>
                )}
                {access.canEditUserSkills && (
                  <DropdownItem
                    onPress={onEditSkills}
                    buttonTestId={`memberships-row-actions-edit-skills-${workspaceId}`}
                  >
                    Edit skills
                  </DropdownItem>
                )}
                {access.canManageUserCapacity && (
                  <DropdownItem
                    onPress={onManageCapacity}
                    buttonTestId={`memberships-row-actions-manage-capacity-${workspaceId}`}
                  >
                    Manage capacity
                  </DropdownItem>
                )}
                {access.canManageWorkspaceMemberships && (
                  <DropdownItem
                    onPress={onRemoveFromWorkspace}
                    buttonTestId={`memberships-row-actions-remove-from-workspace-${workspaceId}`}
                  >
                    Remove from workspace
                  </DropdownItem>
                )}
                {canPromoteToOwner && (
                  <DropdownItem
                    onPress={onPromoteToOwner}
                    buttonTestId={`memberships-row-actions-promote-to-owner-${workspaceId}`}
                  >
                    Promote to Owner
                  </DropdownItem>
                )}
                {(access.canImpersonateAsClient ||
                  access.canImpersonateAsPro) &&
                  !useMagicLink && (
                    <>
                      <DropdownDivider />
                      <DropdownTitle>Impersonation</DropdownTitle>

                      {access.canImpersonateAsClient && (
                        <DropdownItem
                          onPress={onImpersonateAsClient}
                          buttonTestId={`memberships-row-actions-impersonate-as-client-${workspaceId}`}
                        >
                          Impersonate on Customer Portal
                        </DropdownItem>
                      )}

                      {access.canImpersonateAsPro && (
                        <DropdownItem
                          onPress={onImpersonateAsPro}
                          buttonTestId={`memberships-row-actions-impersonate-as-provider-${workspaceId}`}
                        >
                          Impersonate on Work Portal
                        </DropdownItem>
                      )}
                    </>
                  )}

                {useMagicLink && (
                  <>
                    <DropdownDivider />
                    <DropdownTitle>Magic Link</DropdownTitle>

                    {access.canImpersonateAsClient && (
                      <DropdownItem
                        isDisabled={isGenerateMagicLinkPending}
                        onPress={onGenerateMagicLink}
                      >
                        Generate Magic Link
                      </DropdownItem>
                    )}
                  </>
                )}
              </>
            )}
          </Dropdown>
        </div>
      )}
    </div>
  )
}
